import { useMemo, useState } from 'react';
import { Button, InlineNotification, Modal } from '@carbon/react';
import {
  VolumeSourcePicker,
  type VolumeSourceSelection,
} from 'components/volume-source-picker';
import { useVolumeSourcePickerData } from './hooks';
import {
  opensOnFor,
  withPinnedSource,
  type CurrentVolumeSource,
} from './volume-source-utils';
import type { RowVolumeSourcePair } from './utils';
import styles from './edit-volume-source-picker-modal.module.scss';

export interface EditVolumeSourcePickerModalProps {
  /** The slot being edited. */
  slotId: string;
  /** Optional well name for the modal label. */
  wellName?: string;
  /**
   * Whether the slot is a well, as the backend reports it: a row of unknown source type opens
   * on its declines if so, else on its Type Curve Area.
   */
  isWellSlot: boolean;
  /**
   * The slot's overridden volume source, if any. Pre-selected, else the pair's resolved
   * source is; Save stays off until the choice differs from whichever that is.
   */
  currentVolumeSourceId?: string;
  /**
   * What is known about the row's source beyond its id. When the row's own list lacks the
   * source (it was copied from another area or well), it is pinned into that list from this.
   */
  currentSource?: CurrentVolumeSource;
  /** The row's narrowing fields, which key its own forecast list. */
  pair: RowVolumeSourcePair;
  /** Overrides the page's package (the tearsheet can render outside the page provider). */
  packageId?: number;
  onClose: () => void;
  /**
   * The chosen forecast, as the set of fields that travel with it — the routing fields
   * (`volumeSourceType`, `forecastSource`, `dcaScenario`) included.
   */
  onSave: (selection: VolumeSourceSelection) => void;
  /**
   * Clears the slot's volume override. Rendered as a second secondary button only when
   * supplied — a composite wellbore's leg has no other way to drop its override.
   */
  onReset?: () => void;
}

/**
 * The Eval 3.0 inline Edit Volume Source modal (INS-13809): the same picker as Bulk Edit,
 * opened where the row's source lives — its own slot for a decline, its own Type Curve Area
 * for a type curve. From there the user can narrow to another well or area to copy its
 * forecast onto this row. Mount it only while open — it fetches on
 * mount and starts clean each time.
 *
 * @param {EditVolumeSourcePickerModalProps} props - Slot context and callbacks.
 * @return {JSX.Element} The modal.
 */
export function EditVolumeSourcePickerModal({
  slotId,
  wellName,
  isWellSlot,
  currentVolumeSourceId,
  currentSource,
  pair,
  packageId,
  onClose,
  onSave,
  onReset,
}: EditVolumeSourcePickerModalProps) {
  const opensOn = opensOnFor(isWellSlot, currentSource);
  const { picker, reset, onOwnView } = useVolumeSourcePickerData({
    enabled: true,
    ownSlot: { slotId, pair, opensOn },
    packageId,
  });
  // `undefined` = untouched, so the slot's current source shows as chosen; `null` = the user
  // (or an upstream change in the picker) cleared it.
  const [selection, setSelection] = useState<
    VolumeSourceSelection | null | undefined
  >(undefined);

  // The row's source as it stands: its override, else the source it resolves to (the option
  // marked "Current"). Untouched, it shows as chosen; Save stays off until the choice differs
  // from it. Compared against the selection itself, not "was anything reported" — the
  // combobox can echo the pre-selected item back when its list loads.
  const rowSourceId = currentVolumeSourceId ?? pair.resolvedVolumeSourceId;
  const selectedId =
    selection === undefined ? rowSourceId : (selection?.volumeSourceId ?? null);
  const canSave =
    Boolean(selection) && selection?.volumeSourceId !== rowSourceId;

  // A type-curve row with no Type Curve Area of its own opens on an empty area box: there is
  // no list of its own to show, so it says so until the user narrows somewhere — and "Back to
  // this slot" would only return here.
  const noOwnTypeCurves =
    opensOn === 'type_curve' &&
    !pair.typeCurveAreaName &&
    picker.filterBy === 'type_curve_area' &&
    !picker.filterValue;

  // The row's own well reads as the well search lists it ("UWI · Well Name"), not as a bare
  // slot id: the picker never picked it from that list, so it has no label of its own.
  const ownWellLabel =
    picker.filterBy === 'slot_id' && picker.filterValue === slotId && wellName
      ? `${slotId} · ${wellName}`
      : undefined;

  // On the row's own view, its source is always an option: pinned into the list when the
  // row's own endpoint does not return it. Anywhere else the lists are the lookup's, as is.
  // `onOwnView` is the hook's, the same test that picks which endpoint loaded the lists.
  const pickerData = useMemo(
    () => ({
      ...picker,
      filterValueLabel: ownWellLabel,
      inUseId: rowSourceId,
      // An untyped source is pinned into the list the picker opened on, so it shows.
      forecasts: onOwnView
        ? withPinnedSource(picker.forecasts, rowSourceId, {
            ...currentSource,
            type: opensOn,
          })
        : picker.forecasts,
    }),
    [onOwnView, picker, ownWellLabel, rowSourceId, currentSource, opensOn]
  );

  // "Back to this slot" restores the opening state: the narrowing (the hook's reset), the
  // untouched selection, and the picker's own state — both search texts and the archived row —
  // which a remount clears. Keying on a counter is the plain React reset for state a
  // component owns; the alternative, lifting that state or adding an imperative reset to
  // the shared picker, would widen Bulk Edit's contract for an inline-only action.
  const [pickerKey, setPickerKey] = useState(0);
  /** Returns the picker to the row's own view, list and source. */
  const backToOwnSlot = () => {
    reset();
    setSelection(undefined);
    setPickerKey((key) => key + 1);
  };

  /** Saves the chosen forecast with the fields that travel with it. */
  const handleSave = () => {
    if (!selection) return;
    onSave(selection);
  };

  return (
    <Modal
      open
      onRequestClose={onClose}
      modalHeading="Edit Volume Source"
      modalLabel={`Slot ID: ${slotId}${wellName ? ` | Well Name: ${wellName}` : ''}`}
      primaryButtonText="Save"
      primaryButtonDisabled={!canSave}
      onRequestSubmit={handleSave}
      // Carbon renders either the one secondary button or the list; Cancel stays first so a
      // modal with a Reset reads Cancel / Reset / Save.
      {...(onReset
        ? {
            secondaryButtons: [
              { buttonText: 'Cancel', onClick: onClose },
              { buttonText: 'Reset', onClick: onReset },
            ],
          }
        : { secondaryButtonText: 'Cancel' })}
      size="lg"
      className={styles.modal}
    >
      {!onOwnView && !noOwnTypeCurves && (
        <Button
          kind="ghost"
          size="sm"
          className={styles.backToSlot}
          onClick={backToOwnSlot}
        >
          Back to this slot
        </Button>
      )}
      <VolumeSourcePicker
        key={pickerKey}
        idPrefix="inline-volume"
        data={pickerData}
        selectedId={selectedId}
        onSelect={setSelection}
      />
      {noOwnTypeCurves && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="No available Type Curve"
          subtitle="This slot has no Type Curve Area. Search for an area above, or filter by Slot ID."
        />
      )}
    </Modal>
  );
}
